import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  Skeleton,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

// Blockchain Services & Web3
import cinstance from "../../../Service/randomcinstance";
import web3 from "../../../Service/web3";
import {
  networkID,
  sumWeiToEther,
  compareWei,
} from "../../../../static/utils";
import { getNetworkCurrency } from "../../../../static/Constants";
import {
  getConnectedAccount,
  connectWallet as triggerConnectWallet,
  subscribeToWalletEvents,
} from "../../../../static/walletUtils";

// Theme & Common UI
import NavBar from "../../../Components/NavBar";
import { colors, borderRadiusPx, customShadows } from "../../../../theme";
import {
  CommonInput,
  CommonSelect,
  CommonButton,
  NoData,
} from "../../../Components/ui";

// Subcomponents
import SpinHeroBanner from "./components/SpinHeroBanner";
import SpinWheelCard from "./components/SpinWheelCard";
import "./SpinnerLotteryStyle.css";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "highestPrize", label: "Highest Prize Pool" },
  { value: "lowestEntry", label: "Lowest Entry Fee" },
  { value: "capacityHigh", label: "Most Slots" },
  { value: "capacityLow", label: "Fewest Slots (Higher Odds)" },
];

const withTimeout = (promise, ms = 4000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("RPC Request Timeout")), ms),
    ),
  ]);
};

const SpinnerLottery = () => {
  const [loading, setLoading] = useState(true);
  const [lotteries, setLotteries] = useState([]);
  const [partnerMap, setPartnerMap] = useState({});
  const [partnerLogoData, setPartnerLogoData] = useState([]);
  const [account, setAccount] = useState("");
  const [networkId, setNetworkId] = useState(networkID);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const apiUrl = process.env.REACT_APP_API_KEY;
  const currency = useMemo(() => getNetworkCurrency(networkId), [networkId]);

  // Check Account & Network
  const checkAccount = useCallback(async () => {
    const acc = await getConnectedAccount();
    setAccount(acc);
    if (window.ethereum) {
      const netId =
        window.ethereum.networkVersion ||
        (web3 && web3.eth && web3.eth.net
          ? await withTimeout(web3.eth.net.getId(), 2500).catch(() => null)
          : null);
      if (netId) setNetworkId(netId);
    }
  }, []);

  const connectWallet = async () => {
    const res = await triggerConnectWallet();
    if (res.account) {
      setAccount(res.account);
    } else if (res.error === "NO_WALLET") {
      toast.error(res.message);
    } else if (res.isPending) {
      toast.info(res.message);
    } else if (!res.isCancelled && res.message) {
      toast.error(res.message);
    }
  };

  // Fetch all active SpinBET lotteries (strictly status 0 or 5, matching oldAllSpinlottery.js)
  const fetchSpinLotteries = useCallback(async () => {
    setLoading(true);
    try {
      if (window.ethereum) window.web3 = web3;

      const totalIdsRes = await withTimeout(
        cinstance.methods.lotteryId().call(),
        4000,
      ).catch((err) => {
        console.warn("[SpinBET Page] lotteryId() query timed out or failed:", err);
        return 1;
      });
      const totalIds = Number(totalIdsRes) || 1;
      const lotteryPromises = [];

      for (let i = 1; i < totalIds; i++) {
        lotteryPromises.push(
          withTimeout(cinstance.methods.lottery(i).call(), 3500).catch(
            () => null,
          ),
        );
      }

      const results = (await Promise.all(lotteryPromises)).filter(Boolean);

      // Strictly active SpinBET lotteries (Type 0, status 0 [Open] or 5 [Rollover Open])
      const spinList = results.filter(
        (item) =>
          Number(item.lotteryType) === 0 &&
          (item.status === "0" || item.status === "5"),
      );

      setLotteries(spinList);

      // Load partner profiles in parallel for valid non-zero addresses only
      const partnerAddresses = [
        ...new Set(
          spinList
            .map((l) => l.partnerAddress || l.partner)
            .filter(
              (addr) =>
                addr &&
                addr !== "0x0000000000000000000000000000000000000000" &&
                addr !== "0x0",
            ),
        ),
      ];

      if (partnerAddresses.length > 0) {
        const partnerPromises = partnerAddresses.map((addr) =>
          withTimeout(cinstance.methods.partnerbyaddr(addr).call(), 3000)
            .then((res) => ({ [addr]: res }))
            .catch(() => ({ [addr]: null })),
        );

        const partnerEntries = await Promise.all(partnerPromises);
        const mergedPartnerMap = partnerEntries.reduce(
          (acc, curr) => Object.assign(acc, curr),
          {},
        );
        setPartnerMap(mergedPartnerMap);
      } else {
        setPartnerMap({});
      }
    } catch (err) {
      console.error("[SpinBET Page] Error loading SpinBET lotteries:", err);
      toast.error("Error loading on-chain roulette wheels.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch S3 Partner Logos
  useEffect(() => {
    let isMounted = true;
    if (apiUrl) {
      axios
        .get(`${apiUrl}/s3imageupload/getAllPartnerLogos`)
        .then((res) => {
          if (isMounted) {
            setPartnerLogoData(
              res && res.data && res.data.result ? res.data.result : [],
            );
          }
        })
        .catch(() => {
          if (isMounted) setPartnerLogoData([]);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [apiUrl]);

  useEffect(() => {
    checkAccount();
    fetchSpinLotteries();

    const unsubscribe = subscribeToWalletEvents({
      onAccountsChanged: (acc) => setAccount(acc),
      onChainChanged: () => checkAccount(),
      onDisconnect: () => setAccount(""),
    });

    return unsubscribe;
  }, [checkAccount, fetchSpinLotteries]);

  // Combined Jackpot & Stats Calculations
  const combinedJackpot = useMemo(() => {
    return sumWeiToEther(
      lotteries.map((l) => l.totalPrize),
      2,
    );
  }, [lotteries]);

  // Filtered and Sorted list
  const filteredLotteries = useMemo(() => {
    let list = [...lotteries];

    // Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((l) => {
        const idMatch = `${l.lotteryId}`.includes(q);
        const partnerAddr = l.partnerAddress || l.partner || "";
        const partner = partnerMap[partnerAddr];
        const nameMatch =
          partner &&
          ((partner.name && partner.name.toLowerCase().includes(q)) ||
            (partner.companyName && partner.companyName.toLowerCase().includes(q)));
        const addrMatch = partnerAddr.toLowerCase().includes(q);
        return idMatch || nameMatch || addrMatch;
      });
    }

    // Sorting
    list.sort((a, b) => {
      const aCap = Number(a.capacity || 0);
      const bCap = Number(b.capacity || 0);

      switch (sortBy) {
        case "oldest":
          return Number(a.lotteryId) - Number(b.lotteryId);
        case "highestPrize":
          return compareWei(b.totalPrize, a.totalPrize);
        case "lowestEntry":
          return compareWei(a.entryFee, b.entryFee);
        case "capacityHigh":
          return bCap - aCap;
        case "capacityLow":
          return aCap - bCap;
        case "newest":
        default:
          return Number(b.lotteryId) - Number(a.lotteryId);
      }
    });

    return list;
  }, [lotteries, searchQuery, sortBy, partnerMap]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: colors.dashboard.canvas,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <NavBar />

      <Container
        maxWidth="xl"
        className="spinner-page-container"
        sx={{ pt: { xs: "96px", sm: "108px", md: "116px" }, pb: 8, flexGrow: 1 }}
      >
        {/* Hero Header & Live Stats Banner */}
        <SpinHeroBanner
          totalActive={lotteries.length}
          combinedPrize={combinedJackpot}
          currency={currency}
        />

        {/* Filter, Search & Sorting Controls Bar */}
        <Box
          sx={{
            bgcolor: colors.dashboard.cardBg,
            borderRadius: borderRadiusPx.xl,
            border: `1px solid ${colors.dashboard.cardBorder}`,
            p: { xs: 2, md: 2.5 },
            mb: 4,
            boxShadow: customShadows.cardFlat,
          }}
        >
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            {/* Search Field */}
            <Grid item xs={12} sm={6} md={5}>
              <CommonInput
                placeholder="Search by Wheel #ID or Creator name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colors.slate[400], fontSize: 20 }} />
                  </InputAdornment>
                }
                fullWidth
                size="small"
              />
            </Grid>

            {/* Sorting & Refresh */}
            <Grid item xs={12} sm={6} md={5}>
              <Stack
                direction="row"
                spacing={1.5}
                justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                alignItems="center"
              >
                <Box sx={{ minWidth: 180, flexGrow: { xs: 1, sm: 0 } }}>
                  <CommonSelect
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    options={SORT_OPTIONS}
                    size="small"
                  />
                </Box>
                <CommonButton
                  variant="outline"
                  size="small"
                  onClick={() => fetchSpinLotteries()}
                  disabled={loading}
                  sx={{ p: 1, minWidth: 40 }}
                >
                  <RefreshIcon
                    sx={{
                      fontSize: 18,
                      animation: loading ? "spin 1s linear infinite" : undefined,
                    }}
                  />
                </CommonButton>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Live Wheels Grid or Loading Skeletons */}
        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3].map((idx) => (
              <Grid item xs={12} sm={6} lg={4} key={`skel-${idx}`}>
                <Box
                  sx={{
                    bgcolor: colors.dashboard.cardBg,
                    borderRadius: borderRadiusPx.xl,
                    border: `1px solid ${colors.dashboard.cardBorder}`,
                    p: 3,
                    height: 520,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="60%" height={24} />
                      <Skeleton variant="text" width="30%" height={16} />
                    </Box>
                  </Stack>
                  <Skeleton
                    variant="circular"
                    width={220}
                    height={220}
                    sx={{ mx: "auto", my: 2 }}
                  />
                  <Skeleton
                    variant="rounded"
                    height={60}
                    sx={{ borderRadius: borderRadiusPx.md }}
                  />
                  <Skeleton
                    variant="rounded"
                    height={44}
                    sx={{ borderRadius: borderRadiusPx.md }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : filteredLotteries.length === 0 ? (
          <Box sx={{ my: 3 }}>
            <NoData
              message={
                searchQuery
                  ? "No active SpinBET wheels match your search criteria."
                  : "No active SpinBET roulette wheels currently open. Check back soon!"
              }
            />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredLotteries.map((lottery) => {
              const partnerAddr = lottery.partnerAddress || lottery.partner;
              const partnerInfo = partnerMap[partnerAddr];
              return (
                <Grid item xs={12} sm={6} lg={4} key={`wheel-${lottery.lotteryId}`}>
                  <SpinWheelCard
                    lotteryData={lottery}
                    partner={partnerInfo}
                    partnerLogo={partnerLogoData}
                    account={account}
                    currency={currency}
                    onConnectWallet={connectWallet}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Game Rules & FAQ Drawer */}
        <Box sx={{ mt: 4 }}>
          <Accordion
            sx={{
              borderRadius: `${borderRadiusPx.lg} !important`,
              border: `1px solid ${colors.dashboard.cardBorder}`,
              boxShadow: "none",
              backgroundColor: colors.dashboard.cardBg,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <HelpIcon sx={{ color: colors.primary.main, fontSize: 22 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: colors.slate[900] }}
                >
                  How does SpinBET work?
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 3, px: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: colors.slate[50],
                      borderRadius: borderRadiusPx.md,
                      border: `1px solid ${colors.slate[200]}`,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: colors.primary.main, mb: 0.5 }}
                    >
                      1. Pick Your Segment
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.slate[600], lineHeight: 1.6 }}
                    >
                      Select any number from 1 to the wheel capacity (e.g. 1-10 or
                      1-20), or use <strong>Lucky Pick</strong> to randomize your
                      choice.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: colors.slate[50],
                      borderRadius: borderRadiusPx.md,
                      border: `1px solid ${colors.slate[200]}`,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: colors.primary.main, mb: 0.5 }}
                    >
                      2. Spin & Confirm On-Chain
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.slate[600], lineHeight: 1.6 }}
                    >
                      Submit your entry fee via MetaMask. The smart contract
                      generates a verifiable random draw using on-chain randomness.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: colors.slate[50],
                      borderRadius: borderRadiusPx.md,
                      border: `1px solid ${colors.slate[200]}`,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: colors.primary.main, mb: 0.5 }}
                    >
                      3. Instant Win & Payout
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.slate[600], lineHeight: 1.6 }}
                    >
                      If the wheel lands on your selected number, the entire
                      accumulated jackpot is automatically transferred to your wallet
                      instantly!
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Container>
    </Box>
  );
};

export default SpinnerLottery;
